import React, { FunctionComponent, useMemo } from 'react';
import { FormikTextarea } from '../../../capacity/containers/IMODescription/IMODescriptionDrawer';
import Drawer from '../../../common/components/Drawer/Drawer';
import { useSnackbar } from 'notistack';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { McButton, McInputTime, McNotification, McSwitch } from '@maersk-global/mds-react-wrapper';
import z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import FormattedMessage from '../../../common/FormattedMessage';
import { gateHoursService } from '../../../common/service/gate-hours/gate-hours-service';
import { GateHours, GateHoursDailyOverride } from '../../../common/service/gate-hours/types';
import { MappedGateHours } from './gate-hours';
import { createDTO, createDailyOverrideDTOs, groupDailyOverridesByDate } from './helpers';

type GateHoursDrawerProps = {
  open: boolean;
  onSuccess?: Function;
  onClose: () => void;
  selectedTerminal: string;
  defaultValues?: MappedGateHours;
  selectedDaysForEdit: string[];
  data: GateHours;
  openedDateLabel?: string;
  openedDate?: string;
  dailyOverrides?: GateHoursDailyOverride[];
};

const schema = z
  .object({
    selectedDays: z.array(z.string()).min(1, { message: 'Please select at least one day' }),
    open: z.boolean(),
    from: z.string().optional(),
    to: z.string().optional(),
    from1: z.string().optional().nullable(),
    to1: z.string().optional().nullable(),
    from2: z.string().optional().nullable(),
    to2: z.string().optional().nullable(),
    remarks: z.string().optional(),
    fullDayOpen: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.open && !data.fullDayOpen && !data.from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['from'],
      });
    }
    if (data.open && !data.fullDayOpen && !data.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['to'],
      });
    }
    if (data.open && !data.fullDayOpen && data.from1 === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['from1'],
      });
    }
    if (data.open && !data.fullDayOpen && data.to1 === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['to1'],
      });
    }
    if (data.open && !data.fullDayOpen && data.to && data.from1 && data.to > data.from1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Period overlaps the previous one',
        path: ['from1'],
      });
    }
    if (data.open && !data.fullDayOpen && data.from2 === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['from2'],
      });
    }
    if (data.open && !data.fullDayOpen && data.to2 === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['to2'],
      });
    }
    if (data.open && !data.fullDayOpen && data.to1 && data.from2 && data.to1 > data.from2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Period overlaps the previous one',
        path: ['from2'],
      });
    }
    if (data.open && !data.fullDayOpen && data.from && data.to && data.from > data.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please check the time',
        path: ['from'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please check the time',
        path: ['to'],
      });
    }
    if (data.open && !data.fullDayOpen && data.from1 && data.to1 && data.from1 > data.to1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please check the time',
        path: ['from1'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please check the time',
        path: ['to1'],
      });
    }
    if (data.open && !data.fullDayOpen && data.from2 && data.to2 && data.from2 > data.to2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please check the time',
        path: ['from2'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please check the time',
        path: ['to2'],
      });
    }
  });

const buttons = [
  { label: 'M', value: 'MONDAY' },
  { label: 'T', value: 'TUESDAY' },
  { label: 'W', value: 'WEDNESDAY' },
  { label: 'T', value: 'THURSDAY' },
  { label: 'F', value: 'FRIDAY' },
  { label: 'S', value: 'SATURDAY' },
  { label: 'S', value: 'SUNDAY' },
];

const GateHoursDrawer: FunctionComponent<GateHoursDrawerProps> = ({
  open,
  onSuccess,
  onClose,
  selectedTerminal,
  defaultValues,
  selectedDaysForEdit,
  data,
  openedDateLabel,
  openedDate,
  dailyOverrides,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const service = useMemo(() => gateHoursService(), []);

  const toTimeInputValue = (value?: string) => {
    if (!value) return '';
    return value.includes('T') ? value.substring(11, 16) : value.substring(0, 5);
  };

  const getOverrideInitialValues = () => {
    if (!openedDate) return null;

    const groupedOverrides = groupDailyOverridesByDate(dailyOverrides || []);
    const override = groupedOverrides[openedDate];
    if (!override) return null;

    return {
      terminalCode: selectedTerminal,
      from: toTimeInputValue(override.timeSlots[0]?.from),
      to: toTimeInputValue(override.timeSlots[0]?.to),
      from1: override.timeSlots[1] ? toTimeInputValue(override.timeSlots[1].from) : null,
      to1: override.timeSlots[1] ? toTimeInputValue(override.timeSlots[1].to) : null,
      from2: override.timeSlots[2] ? toTimeInputValue(override.timeSlots[2].from) : null,
      to2: override.timeSlots[2] ? toTimeInputValue(override.timeSlots[2].to) : null,
      remarks: override.remarks || '',
      selectedDays: selectedDaysForEdit,
      open: override.fullDayOpen || override.timeSlots.length > 0,
      fullDayOpen: override.fullDayOpen || false,
    };
  };

  const getWeeklyInitialValues = () => {
    const dayKey = selectedDaysForEdit[0] as keyof typeof data.days;
    const dayData = data.days[dayKey];
    const timeSlots = dayData?.timeSlots || [];

    return {
      terminalCode: selectedTerminal,
      from: toTimeInputValue(timeSlots[0]?.from),
      to: toTimeInputValue(timeSlots[0]?.to),
      from1: timeSlots[1] ? toTimeInputValue(timeSlots[1].from) : null,
      to1: timeSlots[1] ? toTimeInputValue(timeSlots[1].to) : null,
      from2: timeSlots[2] ? toTimeInputValue(timeSlots[2].from) : null,
      to2: timeSlots[2] ? toTimeInputValue(timeSlots[2].to) : null,
      remarks: dayData?.remarks || '',
      selectedDays: selectedDaysForEdit,
      open: timeSlots.length > 0,
      fullDayOpen: dayData?.fullDayOpen || false,
    };
  };

  const getFormInitialValues = () => {
    return getOverrideInitialValues() || getWeeklyInitialValues();
  };

  const onSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      if (openedDate) {
        const dailyOverrideDTOs = createDailyOverrideDTOs(openedDate, values);
        const result = await service.updateDailyOverride(selectedTerminal, dailyOverrideDTOs);

        if (result === null) {
          throw new Error('Failed to update daily override');
        }
      } else {
        const dto = createDTO(data, values);
        const result = await service.updateWeatherParam(dto);

        if (!result) {
          throw new Error('Failed to update gate hours');
        }
      }

      enqueueSnackbar('Entry updated successfully', { variant: 'success' });
      onSuccess?.();
      onClose();
    } catch (error) {
      enqueueSnackbar('Unable to update entry', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={openedDateLabel ? `Edit gate hours - ${openedDateLabel}` : 'Edit gate hours'}
      open={open}
      setOpen={onClose}
      onRequestClose={onClose}
      noFooter
    >
      <Formik
        initialValues={getFormInitialValues()}
        enableReinitialize
        validateOnChange={false}
        validationSchema={toFormikValidationSchema(schema)}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values, setFieldValue, errors }) => (
          <Form className="HolidaysDrawer__form">
            <div className="HolidaysDrawer__content">
              {openedDateLabel ? (
                <div className="EventsDrawer__selected-date">{openedDateLabel}</div>
              ) : (
                <>
                  <div className="EventsDrawer__days">
                    {buttons.map((button) => (
                      <McButton
                        key={button.value}
                        label={button.label}
                        appearance={values.selectedDays.includes(button.value) ? 'primary' : 'neutral'}
                        click={() =>
                          setFieldValue(
                            'selectedDays',
                            values.selectedDays.includes(button.value)
                              ? values.selectedDays.filter((x) => x !== button.value)
                              : [...values.selectedDays, button.value],
                          )
                        }
                      />
                    ))}
                  </div>
                  <ErrorMessage className="EventsDrawer__error" name="selectedDays" component="div" />
                </>
              )}
              <br />
              <div className="EventsDrawer__toggle">
                <McSwitch
                  label="Open"
                  change={(e) => {
                    // @ts-ignore
                    const checked = e.target.checked;
                    setFieldValue('open', checked);
                  }}
                  checked={values.open}
                />
              </div>
              <br />
              <div className="EventsDrawer__date-time">
                <div className="EventsDrawer__date-time__input-wrapper">
                  <McInputTime
                    label="From"
                    width="180"
                    invalid={Boolean(errors.from)}
                    errormessage={errors.from as string}
                    value={values.from}
                    // @ts-ignore
                    input={(e) => setFieldValue('from', e.target.value)}
                    disabled={!values.open || values.fullDayOpen}
                  />
                </div>
                <div className="EventsDrawer__date-time__input-wrapper">
                  <McInputTime
                    label="To"
                    width="180"
                    invalid={Boolean(errors.to)}
                    errormessage={errors.to as string}
                    value={values.to}
                    // @ts-ignore
                    input={(e) => setFieldValue('to', e.target.value)}
                    disabled={!values.open || values.fullDayOpen}
                  />
                </div>
                {values.from1 === null ? (
                  <McButton
                    className="EventsDrawer__date-time__add"
                    icon="plus"
                    hiddenlabel
                    variant="outlined"
                    disabled={values.fullDayOpen}
                    click={() => {
                      setFieldValue('from1', '');
                      setFieldValue('to1', '');
                    }}
                  />
                ) : null}
                <div className="switch">
                  <McSwitch
                    label="All day"
                    change={(e) => {
                      // @ts-ignore
                      const checked = e.target.checked;
                      setFieldValue('fullDayOpen', checked);
                    }}
                    checked={values.fullDayOpen}
                  />
                </div>
              </div>
              {values.from1 !== null ? (
                <>
                  <br />
                  <div className="EventsDrawer__date-time">
                    <div className="EventsDrawer__date-time__input-wrapper">
                      <McInputTime
                        label="From"
                        width="180"
                        invalid={Boolean(errors.from1)}
                        errormessage={errors.from1 as string}
                        value={values.from1}
                        // @ts-ignore
                        input={(e) => setFieldValue('from1', e.target.value)}
                        disabled={!values.open || values.fullDayOpen}
                      />
                    </div>
                    <div className="EventsDrawer__date-time__input-wrapper">
                      <McInputTime
                        label="To"
                        width="180"
                        invalid={Boolean(errors.to1)}
                        errormessage={errors.to1 as string}
                        value={values.to1}
                        // @ts-ignore
                        input={(e) => setFieldValue('to1', e.target.value)}
                        disabled={!values.open || values.fullDayOpen}
                      />
                    </div>
                    {values.from2 === null ? (
                      <>
                        <McButton
                          className="EventsDrawer__date-time__add"
                          icon="trash"
                          hiddenlabel
                          appearance="neutral"
                          variant="outlined"
                          disabled={values.fullDayOpen}
                          click={() => {
                            setFieldValue('from1', null);
                            setFieldValue('to1', null);
                          }}
                        />
                        <McButton
                          className="EventsDrawer__date-time__add"
                          icon="plus"
                          hiddenlabel
                          variant="outlined"
                          disabled={values.fullDayOpen}
                          click={() => {
                            setFieldValue('from2', '');
                            setFieldValue('to2', '');
                          }}
                        />
                      </>
                    ) : null}
                  </div>
                </>
              ) : null}
              {values.from2 !== null ? (
                <>
                  <br />
                  <div className="EventsDrawer__date-time">
                    <div className="EventsDrawer__date-time__input-wrapper">
                      <McInputTime
                        label="From"
                        width="180"
                        invalid={Boolean(errors.from2)}
                        errormessage={errors.from2 as string}
                        value={values.from2}
                        // @ts-ignore
                        input={(e) => setFieldValue('from2', e.target.value)}
                        disabled={!values.open || values.fullDayOpen}
                      />
                    </div>
                    <div className="EventsDrawer__date-time__input-wrapper">
                      <McInputTime
                        label="To"
                        width="180"
                        invalid={Boolean(errors.to2)}
                        errormessage={errors.to2 as string}
                        value={values.to2}
                        // @ts-ignore
                        input={(e) => setFieldValue('to2', e.target.value)}
                        disabled={!values.open || values.fullDayOpen}
                      />
                    </div>
                    <McButton
                      className="EventsDrawer__date-time__add"
                      icon="trash"
                      hiddenlabel
                      appearance="neutral"
                      variant="outlined"
                      click={() => {
                        setFieldValue('from2', null);
                        setFieldValue('to2', null);
                      }}
                    />
                  </div>
                </>
              ) : null}
              {values.fullDayOpen && (
                <>
                  <br />
                  <McNotification
                    appearance="info"
                    body={'Gate will be open for the entire day (24 hours).'}
                    icon="info-circle"
                    verticalalign="middle"
                    actionsposition="right"
                  />
                </>
              )}
              <br />
              <Field
                label="Remarks"
                type="text"
                name="remarks"
                rows={2}
                maxlength={100}
                invalid={Boolean(errors.remarks)}
                errormessage={errors.remarks as string}
                component={FormikTextarea}
              />
            </div>
            <div className="HolidaysDrawer__buttons">
              <McButton type="button" appearance="neutral" click={onClose}>
                <FormattedMessage id="cancel" />
              </McButton>
              <McButton disabled={isSubmitting} type="submit" appearance="primary">
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </McButton>
            </div>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export default GateHoursDrawer;
